import React from 'react';
import styled, { useTheme } from 'styled-components';

import { animations, colors, Theme, utils } from '../../../core';
import Heading from '../Heading';
import Icon from '../Icon';
import Text from '../Text';

export interface NotificationProps {
  children: React.ReactNode;
  className?: string;
  width?: string;
  appearance: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  transitionState?: 'entering' | 'entered' | 'exiting' | 'exited';
  variant?: 'primary' | 'secondary';
}

const Box = styled.div<{
  className: string;
  variant: 'primary' | 'secondary';
  backgroundColor: string;
  color: string;
}>`
  align-items: flex-start;
  background-color: ${({ backgroundColor, variant }) =>
    variant === 'primary' ? colors.white : backgroundColor};
  border: 1px solid
    ${({ theme, variant }) =>
      variant === 'primary' ? theme.notificationBorderColor || colors.smoke : 'transparent'};
  border-radius: ${({ theme, variant }) =>
    variant === 'primary' ? 0 : theme.notificationBorderRadius};
  display: flex;
  flex-direction: row;
  padding: 8px 16px;
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.small || '16px'};
  overflow: hidden;

  ${({ variant, color }) =>
    variant === 'primary' &&
    `
    &::after {
      content: '';
      background-color: ${color};
      bottom: -1px;
      display: block;
      height: 4px;
      left: -1px;
      position: absolute;
      right: -1px;
    }
    `}

  ${({ variant, color }) =>
    variant === 'secondary' &&
    `
      justify-content: center;
      align-items: center;

      h6, p {
        color: ${color}
      }
    `}

  &.entering {
    animation: ${animations.fadeAndSlideInFromRight} 220ms forwards ease-out;
  }

  &.exiting {
    animation: ${animations.fadeAndSlideOutToRight} 220ms forwards ease-in;
  }
`;

const Title = styled(Heading).attrs({
  size: 7,
})`
  margin-bottom: 4px;
  padding: 0;
  font-weight: 800;
  ${({ theme }) =>
    theme.name === Theme.theme3 &&
    `
      line-height: 1;
    `}
`;

const Body = styled(Text)`
  margin-bottom: 0;
  line-height: 1.5;
  font-weight: 400;
`;

const TextContainer = styled.div`
  flex: 1;
  margin-left: ${({ theme }) => theme.notificationSpacing || '16px'};
`;

const getColor = (colorKey: string, theme: any) => {
  return theme.notificationColor[colorKey];
};

const getBackgorundColor = (colorKey: string, theme: any) => {
  return theme.notificationBackgroundColor[colorKey];
};

const Notification = ({
  children,
  className,
  appearance,
  transitionState,
  width,
  variant = 'primary',
}: NotificationProps) => {
  const theme = useTheme();

  return (
    <Box
      className={
        className && transitionState
          ? utils.mergeClassNames([className, transitionState]).join(' ')
          : ''
      }
      style={{ width: width || '398px' }}
      color={getColor(appearance, theme)}
      backgroundColor={getBackgorundColor(appearance, theme)}
      variant={variant}
    >
      <Icon
        type={appearance === 'info' ? 'danger' : appearance === 'neutral' ? 'info2' : appearance}
        color={getColor(appearance, theme)}
        size={20}
        viewSize={appearance === 'warning' ? 24 : appearance === 'neutral' ? 19 : 20}
      />
      <TextContainer>{children}</TextContainer>
    </Box>
  );
};

Notification.defaultProps = {
  appearance: 'success',
} as const;

Notification.Title = Title;
Notification.Body = Body;
Notification.create = (title: string, body: string | React.ReactNode) => (
  <React.Fragment>
    {title ? <Title>{title}</Title> : null}
    {typeof body === 'string' ? <Body>{body}</Body> : <React.Fragment>{body}</React.Fragment>}
  </React.Fragment>
);

export default Notification;
